import axios from "axios";
import * as qs from "qs";
import {
  AUTOCOMPLETE_DEBOUNCE_DELAY,
  AUTOCOMPLETE_MIN_INPUT,
  FacultySyncStatusEnums,
  MAX_CSV_EXPORT_RECORDS,
  SUB_OPERATION_STATUS,
} from "app/shared/constants";
import { moreItemsOption } from "app/shared/ui/Autocomplete";

let timeoutId; // ID for API debounce timeout

/**
 * Returns Options values for the parameter passed, for the Selected Submission Id
 */
export const getEditFacultyAutocompleteOptions = async (
  parameter,
  size,
  inputValue,
  setFieldOptions,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    const response = await axios({
      url: `${parameter}s/autocomplete`,
      params: { size: size, value: inputValue },
    });

    const optionsList =
      (response.data.values &&
        response.data.values.map((responseObj) => responseObj.value)) ||
      [];

    setFieldOptions(optionsList);
  } catch (error) {
    //Basic error handling added here as the functionality does not require to display case specific error messages.
    setAlert("error", error.message);
  }
};
export const getSubmissionsList = async (
  setSubmissionList,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    const request = {
      url: "/submissions",
      params: {
        vw: "brief",
        ps: 500,
      },
    };

    const response = await axios(request);
    const { values = [], count = 0 } = response.data;
    if (values.length > 0 && count > 0) {
      // Filter only the submissions with operation status as READY
      const readySubmissions = values.filter(
        (sub) => sub.operationStatus === SUB_OPERATION_STATUS.READY,
      );
      setSubmissionList(readySubmissions);
    }
  } catch (error) {
    //Basic error handling added here as the functionality does not require to display case specific error messages.
    setAlert("error", error.message);
  }
};

/**
 * API call to Export CSV
 */
export const getFacultyExportData = async (
  submission,
  facultyPreference,
  setAlert,
  clearAlert,
  totalCount,
  setExportLoading,
) => {
  setExportLoading(true);
  try {
    clearAlert();
    const { filters, sort } = facultyPreference.facultyDemographics;
    const {
      facultyUniversityId,
      degreeCode,
      rankCode,
      trainingRole,
      departmentCode,
    } = filters;

    const { orderBy, order } = sort;

    let request = "";
    if (submission != null) {
      request = {
        url: `/submissions/${submission}/faculty`,
        params: {
          facultyUniversityIds: facultyUniversityId,
          degreeCode: degreeCode?.code,
          rankCode: rankCode?.code,
          trainingRole,
          departmentCode: departmentCode?.code,
          p: 1,
          ps: totalCount,
          s: `${orderBy ? `${orderBy}` : `fullName`}:${
            order ? `${order}` : `asc`
          }`,
        },
        responseType: "blob",
        headers: {
          Accept: "application/vnd.ms-excel",
          "Content-type": "application/vnd.ms-excel",
        },
      };
    }

    const response = await axios(request);

    if (response.data) {
      const currentDate = new Date();
      const month = ("0" + (currentDate.getMonth() + 1)).slice(-2);
      const date = ("0" + currentDate.getDate()).slice(-2);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Export_Faculty_${currentDate.getFullYear()}${month}${date}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
    }
  } catch (error) {
    //Basic error handling added here as the functionality does not require to display case specific error messages.
    setAlert("error", error.message);
  } finally {
    setExportLoading(false);
  }
};

export const getFacultyFiltersOptionValues = async (
  setDegreeList,
  setRankList,
  setProgramList,
  setTrainingRoleList,
  selectedSubmission,
  setAlert,
  clearAlert,
  getSelectedSubmissionName,
  t,
) => {
  // **** Selected Submission Id ****

  try {
    //clearAlert();
    if (selectedSubmission) {
      const response = await axios({
        url: `/submissions/${selectedSubmission}/faculty/filters`,
      });

      const { degrees, facultyPrograms, ranks, trainingRoles } = response.data;

      // Helper Function to set Options in Filter Fields
      const getFieldValues = (trainingRoles) => {
        let valueArray = [];
        if (trainingRoles.count > 0) {
          valueArray = trainingRoles.values.map((filterVal) => filterVal.value);
        }

        return valueArray;
      };

      setDegreeList(degrees.values ?? []);
      setRankList(ranks.values ?? []);
      setProgramList(facultyPrograms.values ?? []);
      setTrainingRoleList(getFieldValues(trainingRoles));
    }
  } catch (error) {
    if (
      error.status === 404 &&
      (error.code === "RESOURCE_NOT_FOUND" ||
        error.code === "RESOURCE_UNKNOWN") &&
      error.response?.data?.detail?.properties[0]?.resource === "Submission"
    ) {
      const submissionName = getSelectedSubmissionName();
      setAlert(
        "error",
        t("submission.create.notification.submissionNotFoundError", {
          submissionName,
        }),
      );
    } else {
      setAlert("error", error.message);
    }
  }
};

/**
 * Returns List of Faculty for the input value entered
 *
 * @param {submissionId} submissionId - Input value for submissionId
 * @param {string} facultyVal - Input Value for Faculty
 * @param {string} setFacultyList - Setter function to set Faculty List
 */
export const loadSubmissionFacultyList = (
  submissionId,
  facultyVal,
  setFacultyList,
  setAlert,
  clearAlert,
  setNoOptionsText,
  t,
) => {
  if (facultyVal.length < AUTOCOMPLETE_MIN_INPUT) {
    setFacultyList([]);
    setNoOptionsText(t("globals.autocomplete.helperText"));
    return;
  }

  clearTimeout(timeoutId); // Cancel current debounce timer

  timeoutId = setTimeout(async () => {
    try {
      clearAlert();

      const fetchSize = 50;
      const request = {
        method: "get",
        url: `/submissions/${submissionId}/faculty/autocomplete`,
        params: {
          value: facultyVal,
          size: fetchSize,
        },
      };

      const response = await axios(request);
      const facultyList = response.data.values;

      if (facultyList && facultyList.length >= fetchSize) {
        facultyList.push(moreItemsOption(fetchSize, t));
      }

      setFacultyList(facultyList || []);

      if (response.data.count === 0) {
        setNoOptionsText(t("faculty.autocomplete.noResultsText"));
      }
    } catch (error) {
      //Basic error handling added here as the functionality does not require to display case specific error messages.
      setAlert("error", error.message);
    }
  }, AUTOCOMPLETE_DEBOUNCE_DELAY);
};

/**
 * Returns List of Faculty for the input value entered
 *
 * @param {string} facultyVal - Input Value for Faculty
 * @param {string} setFacultyList - Setter function to set Faculty List
 */
export const loadFacultyList = (
  facultyVal,
  setFacultyList,
  setAlert,
  clearAlert,
  setNoOptionsText,
  t,
  submissionId,
) => {
  if (facultyVal.length < AUTOCOMPLETE_MIN_INPUT) {
    setFacultyList([]);
    setNoOptionsText(t("globals.autocomplete.helperText"));
    return;
  }

  clearTimeout(timeoutId); // Cancel current debounce timer

  timeoutId = setTimeout(async () => {
    try {
      clearAlert();

      const fetchSize = 50;
      const request = {
        method: "get",
        url: "/faculty/autocomplete",
        params: {
          value: facultyVal,
          size: fetchSize,
          submissionId,
        },
        paramsSerializer: {
          serialize: (params) => {
            return qs.stringify(params, { arrayFormat: "repeat" });
          },
        },
      };

      const response = await axios(request);
      const facultyList = response.data.values;

      if (facultyList && facultyList.length >= fetchSize) {
        facultyList.push(moreItemsOption(fetchSize, t));
      }

      setFacultyList(facultyList || []);

      if (response.data.count === 0) {
        setNoOptionsText(t("faculty.autocomplete.noResultsText"));
      }
    } catch (error) {
      //Basic error handling added here as the functionality does not require to display case specific error messages.
      setAlert("error", error.message);
    }
  }, AUTOCOMPLETE_DEBOUNCE_DELAY);
};

/**
 * Fetches Faculty queueStatus Updates
 * @param {string} selectedSubmission - Selected Submission Id
 * @param {string} pendingFacultyUniversityIds - list of Faculty university Ids with queue status not success
 * @param {function} setData - Setter function to set Faculty List
 */

export const syncFacultyStatus = async (
  selectedSubmission,
  pendingFacultyUniversityIds,
  setData,
  setAlert,
  t,
) => {
  try {
    const response = await axios({
      url: `/submissions/${selectedSubmission}/faculty`,
      params: { facultyUniversityIds: pendingFacultyUniversityIds },
    });

    const values = response.data.values ?? [];
    // Update the Faculty List
    setData((prevData) =>
      prevData.map((faculty) => {
        const updated = values.find(
          (f) => f.universityId === faculty.universityId,
        );
        return updated ? { ...faculty, ...updated } : faculty;
      }),
    );
    // Set alert message for queueStatus===ERROR
    values.forEach((faculty) => {
      if (faculty.queueStatus === FacultySyncStatusEnums.ERROR) {
        setAlert(
          "error",
          t("faculty.notification.errorAddFaculty", {
            facultyName: `"${faculty.facultyFullName.trim()}"`,
            support: t("app.supportEmail"),
          }),
          false,
        );
      }
    });
  } catch (error) {
    setAlert("error", error.message);
  }
};

/**
 * Returns List of Faculty Demographics details,
 * for selected submission
 */
export const getFacultyDemographicsDetails = async (
  setData,
  setSummary,
  setIsLoading,
  selectedSubmission,
  facultyPreference,
  navigate,
  setAlert,
  clearAlert,
  setMetaData,
  getSelectedSubmissionName,
  t,
) => {
  const { filters, pagination, sort } = facultyPreference.facultyDemographics;
  const { facultyName, degreeCode, rankCode, trainingRole, departmentCode } =
    filters;
  const { page, pageSize } = pagination;
  const { orderBy, order } = sort;

  try {
    //clearAlert();
    setIsLoading(true);

    if (selectedSubmission) {
      const response = await axios({
        url: `/submissions/${selectedSubmission}/faculty`,
        params: {
          facultyUniversityIds: facultyName?.value?.universityId,
          degreeCode: degreeCode?.code,
          rankCode: rankCode?.code,
          trainingRole,
          departmentCode: departmentCode?.code,
          p: +page + 1,
          ps: pageSize || 10,
          s: `${orderBy ? `${orderBy}` : `fullName`}:${
            order ? `${order}` : `asc`
          }`,
        },
      });

      const values = response.data.values ?? [];
      const exportMessage =
        response.data.totalCount > MAX_CSV_EXPORT_RECORDS
          ? t("globals.metaDataInfo.exportTitleTooMany")
          : t("globals.metaDataInfo.exportTitleNormal");

      setData(values);
      setMetaData(response.data.meta);
      setSummary({
        totalCount: response.data.totalCount,
        exportMessage: exportMessage,
      });
    } else {
      setData([]);
      setSummary({
        totalCount: 0,
        exportMessage: t("globals.metaDataInfo.noData"),
      });
      setMetaData(false);
    }
  } catch (error) {
    // check if the error is due to submission not found
    if (
      error.status === 404 &&
      (error.code === "RESOURCE_NOT_FOUND" ||
        error.code === "RESOURCE_UNKNOWN") &&
      error.response?.data?.detail?.properties[0]?.resource === "Submission"
    ) {
      const submissionName = getSelectedSubmissionName();
      // set alert message for submission not found
      setAlert(
        "error",
        t("submission.create.notification.submissionNotFoundError", {
          submissionName,
        }),
      );
    } else {
      // set alert message for other errors
      setAlert("error", error.message);
    }
  } finally {
    setIsLoading(false);
  }
};

export const saveFacultySelection = async (
  setFacultyPreference,
  facultyPreference,
  universityId,
  selectedSubmission,
  setAlert,
  clearAlert,
  setLoading,
  t,
  isAutoLoadEnabled,
) => {
  try {
    setLoading(true);
    clearAlert();

    const request = {
      method: "post",
      url: `/submissions/${selectedSubmission}/faculty`,
      params: {
        universityId,
      },
    };

    const response = await axios(request);

    setFacultyPreference({
      ...facultyPreference,
      facultyDemographics: {
        ...facultyPreference.facultyDemographics,
        addFaculty: facultyPreference.facultyDemographics.addFaculty + 1,
      },
    });

    if (isAutoLoadEnabled) {
      setAlert(
        "success",
        t("faculty.notification.addFacultyWithAutoLoad", {
          facultyName: `"${response.data.facultyFullName.trim()}"`,
        }),
        false,
      );
    } else {
      setAlert(
        "success",
        t("faculty.notification.addFaculty", {
          facultyName: `"${response.data.facultyFullName.trim()}"`,
        }),
        false,
      );
    }
  } catch (error) {
    if (error.code === "RESOURCE_EXISTS" && error.status === 409) {
      setAlert("error", t("faculty.notification.duplicateFaculty"));
    } else if (error.code === "RESOURCE_NOT_FOUND" && error.status === 404) {
      setAlert(
        "error",
        t("faculty.notification.facultyNotFound", {
          facultyName: universityId,
        }),
      );
    } else if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      setAlert("error", t("faculty.notification.addTimeout"));
    } else {
      setAlert("error", error.message);
    }
  } finally {
    setLoading(false);
  }
};

/**
 * Delete Participating Faculty corresponding to Submission
 */
export const deleteParticipatingFaculty = async (
  deleteType,
  deleteUrl,
  setSnackBar,
  setFacultyPreference,
  facultyPreference,
  setAlert,
  clearAlert,
  setLoading,
  t,
) => {
  let deleteFacultyResponse = "";
  try {
    setLoading(true);
    clearAlert();
    deleteFacultyResponse = await axios({
      method: "DELETE",
      url: `${deleteUrl}`,
    });

    if (deleteFacultyResponse.status === 204) {
      deleteType === "All"
        ? setSnackBar({
            show: true,
            message: "faculty.delete.deleteAllSuccessMessage",
          })
        : setSnackBar({
            show: true,
            message: "faculty.delete.deleteSuccessMessage",
          });

      setFacultyPreference({
        ...facultyPreference,
        facultyDemographics: {
          ...facultyPreference.facultyDemographics,
          delete: facultyPreference.facultyDemographics.delete + 1,
        },
      });
    }
  } catch (error) {
    //Basic error handling added here as the functionality does not require to display case specific error messages.
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      setAlert("error", t("faculty.notification.deleteTimeout"));
    } else {
      setAlert("error", error.message);
    }
  } finally {
    setLoading(false);
  }
};

/**
 * Returns Faculty values to be Edited, for the Selected Submission Id
 **/
export const getEditFacultyDetailsById = async (
  setFacultyEditFormValues,
  setFacultyDetailsFetchError,
  setLoading,
  facultyId,
  selectedSubmission,
  editFacultyInitialValues,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    setLoading(true);
    const response = await axios({
      url: `/submissions/${selectedSubmission}/faculty/${facultyId}`,
    });
    response.data.trainingRoles =
      (response.data.trainingRoles &&
        response.data.trainingRoles.map((val) => val.role)) ||
      [];
    setFacultyEditFormValues({ ...editFacultyInitialValues, ...response.data });
  } catch (error) {
    setFacultyDetailsFetchError("RESOURCE_NOT_FOUND");
    setAlert("error", error.message);
  } finally {
    setLoading(false);
  }
};
export const getFacultyPrimaryDepartmentById = async (
  setFacultyPrimaryDepartmentValues,
  facultyId,
  selectedSubmission,
  facultyPrimaryDepartmentValues,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    const response = await axios({
      url: `/submissions/${selectedSubmission}/faculty/${facultyId}/departments`,
    });
    setFacultyPrimaryDepartmentValues({
      ...facultyPrimaryDepartmentValues,
      ...response.data,
    });

    return response.data;
  } catch (error) {
    setAlert("error", error.message);
  }
};

/**
 * Returns Success/Error Response Data for the Update Faculty API call
 *
 * @param {string} submissionId - Selected Submission ID
 * @param {string} values  - Data to be Updated for the Faculty
 */
export const putFacultyDetailsByIdUpdate = async (
  setCommonsIdFieldError,
  submissionId,
  values,
  navigate,
  setAlert,
  clearAlert,
  t,
) => {
  /* Do not mutate original Formik.values object,
   * Created a new object (with a new reference) to submit the form values
   *  This is needed, to keep the original Form values intact,
   *  as in case of any API Error on Form Submit,
   *  original Form state should be available,as it was before Submit.
   */

  try {
    clearAlert();
    let editFacultyFormValues = Object.assign({}, values);

    if (editFacultyFormValues.trainingRoles) {
      editFacultyFormValues.trainingRoles =
        editFacultyFormValues.trainingRoles.map((val) => ({
          displayText: val,
          role: val,
        }));
    }
    // ** Request Object **
    const request = {
      method: "PUT",
      url: `/submissions/${submissionId}/faculty`,
      data: { ...editFacultyFormValues },
    };

    const response = await axios(request);

    // **** Redirect to /faculty on Successful form Submit ****
    navigate({
      pathname: "/faculty",
    });
    setAlert(
      "success",
      t("faculty.notification.updateFaculty", {
        facultyName: `"${response.data.facultyFullName.trim()}"`,
      }),
      true,
    );
  } catch (error) {
    if (error.status === 409) {
      setCommonsIdFieldError({ value: values.commonsId, isError: true });
    } else {
      setAlert("error", error.message);
    }
  }
};
